<script setup lang="ts">
import type { OrderInvoiceFields } from '../../model/invoice/interfaces'
import type { AppTextareaEvent } from '@shared/ui/fields'
import type { InputPrimaryEvent } from '@shared/ui/fields'

import { faEye } from '@fortawesome/pro-regular-svg-icons'

import { AppIcon } from '@shared/ui/icons'
import { AppText } from '@shared/ui/typography'
import { AppTextarea } from '@shared/ui/fields'
import { BtnSecondary } from '@shared/ui/buttons'
import { InputPrimary } from '@shared/ui/fields'

import { getInvoiceItemLabel } from '../../lib/invoice-fields'

defineOptions({
  name: 'OrderInvoiceFields',
})

defineProps<{
  fields: OrderInvoiceFields
  readonly: boolean
  canPreview: boolean
  previewDisabled: boolean
}>()

const emit = defineEmits<{
  'change:parent-full-name': [value: string]
  'change:address': [value: string]
  'change:item-details': [orderItemId: number, value: string]
  'click:preview': []
}>()

const OPTIONAL_LABEL = '(optional)'
const NAME_MAX_LENGTH = 255
const ADDRESS_MAX_LENGTH = 255
const DETAILS_MAX_LENGTH = 200

const getDetailsHint = (details: string | null): string => `${(details ?? '').length}/${DETAILS_MAX_LENGTH}`

const handleParentFullName = (event: InputPrimaryEvent): void => emit('change:parent-full-name', String(event.value))

const handleAddress = (event: AppTextareaEvent): void => emit('change:address', event.value)

const handleItemDetails = (orderItemId: number, event: AppTextareaEvent): void =>
  emit('change:item-details', orderItemId, event.value)
</script>

<template>
  <div class="order-invoice-fields">
    <AppText name="body1-medium">
      Invoice fields
    </AppText>
    <InputPrimary
      id="order-invoice-parent-full-name"
      label="Parent full name"
      :optional-label="OPTIONAL_LABEL"
      :value="fields.parent_full_name ?? ''"
      :max-length="NAME_MAX_LENGTH"
      :readonly="readonly"
      placeholder="Type here"
      @input="handleParentFullName"
    />
    <AppTextarea
      id="order-invoice-address"
      label="Address"
      :optional-label="OPTIONAL_LABEL"
      :value="fields.address ?? ''"
      :max-length="ADDRESS_MAX_LENGTH"
      :readonly="readonly"
      placeholder="Type here"
      @input="handleAddress"
    />
    <AppTextarea
      v-for="item in fields.items"
      :id="`order-invoice-details-${item.order_item_id}`"
      :key="item.order_item_id"
      :label="getInvoiceItemLabel(item)"
      :optional-label="OPTIONAL_LABEL"
      :value="item.details ?? ''"
      :max-length="DETAILS_MAX_LENGTH"
      :hint="getDetailsHint(item.details)"
      :readonly="readonly"
      placeholder="Type here"
      @input="(event: AppTextareaEvent) => handleItemDetails(item.order_item_id, event)"
    />
    <BtnSecondary
      v-if="canPreview"
      text="Preview"
      color="blue"
      :disabled="previewDisabled"
      @click="$emit('click:preview')"
    >
      <template #iconLeft>
        <AppIcon :icon="faEye" />
      </template>
    </BtnSecondary>
  </div>
</template>

<style scoped lang="scss" src="./OrderInvoiceFields.scss" />
